import {
  DocumentData,
  QueryDocumentSnapshot,
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  documentId,
} from 'firebase/firestore';
import NumberDoc from '../../models/NumberDoc';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import NumberOptions from '../../types/NumberOptions';

async function getNumbers(
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  options?: NumberOptions
): Promise<{
  numbers: NumberDoc[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
  availabilityCounts?: {
    available: number;
    assigned: number;
  };
}> {
  try {
    const db = firestore;
    const numbersCollection = collection(db, FirestoreCollections.numbers);

    const numbersConstraints = [];
    const excludedCountConstraints = [];

    // Add availability option
    if (options?.available !== undefined) {
      excludedCountConstraints.push(where('available', '==', options.available));
    }

    // Add channel option
    if (options?.channel) {
      numbersConstraints.push(where('channel', '==', options.channel));
    }

    // Add product option
    if (options?.product) {
      numbersConstraints.push(where('product', '==', options.product));
    }

    // Add prefix filter using documentId range query (e.g., '337' for 07 numbers)
    if (options?.prefixFilter) {
      const prefix = options.prefixFilter;
      // Calculate the end of the range by incrementing the last character
      const prefixEnd =
        prefix.slice(0, -1) + String.fromCharCode(prefix.charCodeAt(prefix.length - 1) + 1);
      numbersConstraints.push(where(documentId(), '>=', prefix));
      numbersConstraints.push(where(documentId(), '<', prefixEnd));
    }

    // Use custom limit if provided, otherwise use the offset parameter
    const queryLimit = options?.limit ?? offset;

    // If shuffle is enabled, fetch more numbers and randomly select the requested amount
    const fetchLimit = options?.shuffle ? Math.max(queryLimit * 10, 50) : queryLimit;

    const results = lastVisible
      ? await getDocs(
          query(
            numbersCollection,
            ...excludedCountConstraints,
            ...numbersConstraints,
            orderBy(documentId()),
            startAfter(lastVisible),
            limit(fetchLimit)
          )
        )
      : await getDocs(
          query(
            numbersCollection,
            ...excludedCountConstraints,
            ...numbersConstraints,
            orderBy(documentId()),
            limit(fetchLimit)
          )
        );

    let numbers: NumberDoc[] = [];
    results.docs.forEach((doc) => {
      numbers.push(NumberDoc.fromFirestore(doc));
    });

    // Shuffle and limit results if requested
    if (options?.shuffle && numbers.length > 0) {
      // Fisher-Yates shuffle
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      // Take only the requested limit
      const requestedLimit = options?.limit ?? queryLimit;
      numbers = numbers.slice(0, requestedLimit);
    }

    const numbersSnapshot = await getCountFromServer(numbersCollection);

    if (options) {
      const availableNumbersSnapshot = await getCountFromServer(
        query(numbersCollection, ...numbersConstraints, where('available', '==', true))
      );

      const assignedNumbersSnapshot = await getCountFromServer(
        query(numbersCollection, ...numbersConstraints, where('available', '==', false))
      );

      return {
        numbers,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: numbersSnapshot.data().count ?? 0,
        availabilityCounts: {
          available: availableNumbersSnapshot.data().count ?? 0,
          assigned: assignedNumbersSnapshot.data().count ?? 0,
        },
      };
    }

    return {
      numbers,
      lastVisible: results.docs[results.docs.length - 1],
      totalCollectionCount: numbersSnapshot.data().count ?? 0,
    };
  } catch (e) {
    console.error('error: ', e);
    throw new GetNumbersFailure();
  }
}

export class GetNumbersFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getNumbers;
